import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useParams } from '@tanstack/react-router';
import { BodyBadge } from '@/components/BodyBadge';
import { Icon } from '@/components/Icon';
import {
  HowRecordsAreBuilt,
  shouldAutoOpenExplainer,
} from '@/features/records/HowRecordsAreBuilt';
import { fetchRecord } from '@/lib/api';
import { isHardMatch, MATCH_BASIS_LABEL, STAGES, type LegislativeRecord } from '@/lib/records';
import './Records.css';

/**
 * One record: the timeline, and why these items are one record.
 *
 * The provenance panel is not decoration. A record is an assertion that several meetings are
 * about the same thing, and the user has to be able to check that assertion. Hard joins on a
 * shared ordinance number are shown green; inferred joins on a shared name or parcel are shown
 * amber and say what they matched on. Never collapse the two into one confidence score.
 */
export function RecordDetail() {
  const { recordId } = useParams({ from: '/legislative-history/$recordId' });
  const { data, isLoading, isError } = useQuery({
    queryKey: ['record', recordId],
    queryFn: () => fetchRecord(recordId),
  });

  const [howOpen, setHowOpen] = useState(false);

  // First visit to the surface gets the explainer unprompted — the origin of a record was
  // found to be unclear without it.
  useEffect(() => {
    if (shouldAutoOpenExplainer()) setHowOpen(true);
  }, []);

  if (isLoading) return <p className="records__empty">Loading record…</p>;
  if (isError || !data) return <p className="records__empty">Couldn’t load that record.</p>;

  const stageIndex = STAGES.indexOf(data.stage);

  return (
    <div className="record">
      <header className="record__head">
        <Link to="/legislative-history" className="record__back">
          <Icon name="chevron-left" /> Legislative History
        </Link>

        <div className="record__title">
          <span className="records__num">
            {data.num ?? <em className="records__noNum">No number yet</em>}
          </span>
          <h1>{data.label}</h1>
          <p className="record__where">
            <BodyBadge body={data.body} short /> {data.townName}, {data.townRegion} ·{' '}
            {data.meetingCount} meetings · {data.firstSeen} – {data.lastSeen}
          </p>
          <p className="record__status">{data.status}</p>
        </div>

        {/* Stage lineage. The current stage is filled; later stages are outlined, so a reader
            can see both where it is and where it has left to go. */}
        <ol className="record__stages">
          {STAGES.map((stage, i) => (
            <li key={stage} className="record__stage" data-state={i < stageIndex ? 'done' : i === stageIndex ? 'current' : 'ahead'}>
              {stage}
            </li>
          ))}
        </ol>
      </header>

      <div className="record__panes">
        <section className="record__timeline" aria-label="Timeline">
          {data.items.map((item) => {
            const hard = isHardMatch(item.basis);
            return (
              <article key={item.meetingId} className="record__item" data-hard={hard}>
                <div className="record__itemHead">
                  <span className="record__itemDate">{item.date}</span>
                  {item.type && <span className="record__itemType">{item.type}</span>}
                  {item.num ? (
                    <span className="record__itemNum">{item.num}</span>
                  ) : (
                    <span className="record__itemNoNum">No number yet</span>
                  )}
                </div>

                <h2 className="record__itemName">{item.name}</h2>
                <p className="record__itemSummary">{item.summary}</p>

                <div className="record__itemFoot">
                  {/* Vote is read from the record, never inferred. Null means discussed with
                      no vote taken, which is a fact worth stating rather than omitting. */}
                  {item.vote ? (
                    <span className="record__vote" data-voted="true">
                      {item.vote}
                    </span>
                  ) : (
                    <span className="record__vote" data-voted="false">
                      No vote taken
                    </span>
                  )}
                  <Link
                    to="/meeting/$meetingId"
                    params={{ meetingId: item.meetingId }}
                    className="record__open"
                  >
                    Open meeting
                  </Link>
                </div>
              </article>
            );
          })}
        </section>

        <aside className="record__why" aria-label="Why these items are one record">
          <div className="record__whyHead">
            <h2 className="u-meta">Why these items are one record</h2>
            <button type="button" className="record__howLink" onClick={() => setHowOpen(true)}>
              How records are built
            </button>
          </div>

          <dl className="record__facts">
            <dt>Identifier</dt>
            <dd>{data.num ?? 'None spoken yet'}</dd>
            <dt>Origin</dt>
            <dd>{data.signalName ? `Signal · ${data.signalName}` : 'Added by you'}</dd>
            <dt>Span</dt>
            <dd>
              {data.firstSeen} – {data.lastSeen}
            </dd>
          </dl>

          <h3 className="u-meta record__whySub">What links each item</h3>
          <ul className="record__signals">
            {data.items.map((item) => {
              const hard = isHardMatch(item.basis);
              return (
                <li key={item.meetingId} className="record__signal" data-hard={hard}>
                  <span className="record__signalDot" />
                  <span className="record__signalBody">
                    <span className="record__signalBasis">{MATCH_BASIS_LABEL[item.basis]}</span>
                    <span className="record__signalDetail">
                      {hard ? (
                        <>{item.num}</>
                      ) : (
                        <>
                          matched on “{item.matchedOn}”
                          {item.matchScore !== null && (
                            <> · {Math.round(item.matchScore * 100)}% name similarity</>
                          )}
                        </>
                      )}
                    </span>
                    <span className="record__signalDate">{item.date}</span>
                  </span>
                </li>
              );
            })}
          </ul>

          <p className="record__legend">
            <span className="record__signalDot" data-hard="true" /> A shared number is a fact.
            <br />
            <span className="record__signalDot" data-hard="false" /> A shared name or parcel is
            an inference — worth reading, worth checking.
          </p>
        </aside>
      </div>

      {howOpen && <HowRecordsAreBuilt onClose={() => setHowOpen(false)} />}
    </div>
  );
}

export type { LegislativeRecord };
